import React from "react";
import BookingCalendar from "./booking_calendar";
import { fetchFirstAvailableMonth, assignFirstAvailable } from "../../util/first_available_api_util";
import { railsToJsTimezoneMap } from "../../util/timezone_utils";
import { formatTime, formatDatetime } from "../../util/date_util";

// /book first-available: shows only dates and times a local photographer who
// can do every service in the cart is free, then assigns one by rotation.
const pad = (n) => String(n).padStart(2, "0");
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const DEFAULT_TZ = "Eastern Time (US & Canada)";

export default class FirstAvailablePicker extends React.Component {
  constructor(props) {
    super(props);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.firstMonth = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), 1);
    this.state = { month: this.firstMonth, days: {}, loading: true, selectedDate: null, selected: null, assigning: false, error: null };
    this.requestId = 0;
  }

  componentDidMount() {
    document.body.style.overflow = "hidden";
    this.load(this.firstMonth, true);
  }

  componentWillUnmount() {
    document.body.style.overflow = "unset";
  }

  baseParams() {
    const { services, packageAttributes, appointments, selectedService } = this.props;
    const twilight = !!services[selectedService]?.twilight;
    if (this.props.serviceIdsOverride) {
      return {
        service_ids: this.props.serviceIdsOverride, zipcode: this.props.zipcode, timezone: this.props.timezone || DEFAULT_TZ,
        twilight, long_shoot: !!this.props.longShoot, photographer_id: this.props.photographerId || undefined
      };
    }
    const serviceIds = twilight
      ? [selectedService]
      : Object.keys(packageAttributes).filter(id => !services[id]?.twilight && !appointments[id]);
    if (!twilight && !serviceIds.map(String).includes(String(selectedService))) serviceIds.push(selectedService);
    return {
      service_ids: serviceIds, zipcode: this.props.zipcode, timezone: this.props.timezone || DEFAULT_TZ,
      twilight, long_shoot: !!this.props.longShoot, photographer_id: this.props.photographerId || undefined
    };
  }

  load(month, autoSelect = false) {
    const id = ++this.requestId;
    this.setState({ month, loading: true, selectedDate: null, selected: null, error: null });
    fetchFirstAvailableMonth({ ...this.baseParams(), month: monthKey(month) })
      .then(({ days }) => {
        if (id !== this.requestId) return;
        const first = Object.keys(days || {}).sort()[0] || null;
        this.setState({ days: days || {}, loading: false, selectedDate: autoSelect || first ? first : null });
        // Warm the server cache for next month so the arrow click is instant.
        const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
        fetchFirstAvailableMonth({ ...this.baseParams(), month: monthKey(next) }).catch(() => {});
      })
      .catch(() => {
        if (id !== this.requestId) return;
        this.setState({ days: {}, loading: false, error: "We couldn't load open times. Please try again." });
      });
  }

  changeMonth(delta) {
    const next = new Date(this.state.month.getFullYear(), this.state.month.getMonth() + delta, 1);
    if (next < this.firstMonth) return;
    this.load(next);
  }

  confirm = () => {
    const { selected } = this.state;
    if (!selected || this.state.assigning) return;
    this.setState({ assigning: true, error: null });
    assignFirstAvailable({ ...this.baseParams(), start_time: selected.start_time })
      .then(({ contractor, token }) => {
        this.setState({ assigning: false });
        this.props.onPicked(new Date(selected.start_time), contractor, token);
      })
      .catch((xhr) => {
        this.setState({ assigning: false, error: xhr?.responseJSON?.error || "That time was just taken. Please pick another time." });
        this.load(this.state.month);
      });
  };

  render() {
    const { month, days, loading, selectedDate, selected, assigning, error } = this.state;
    const tz = this.props.timezone || DEFAULT_TZ;
    const jsTz = railsToJsTimezoneMap[tz] || (tz.includes("/") ? tz : "America/New_York");
    const twilight = !!this.props.services[this.props.selectedService]?.twilight;

    const cells = [];
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    for (let i = 0; i < month.getDay(); i++) cells.push({ key: `blank-${i}`, blank: true });
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${monthKey(month)}-${pad(d)}`;
      const open = !loading && !!days[iso];
      const date = new Date(month.getFullYear(), month.getMonth(), d);
      cells.push({
        key: iso, num: d, disabled: !open, marked: open, selected: open && selectedDate === iso,
        ariaLabel: `${date.toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric" })}, ${open ? "times available" : "no open times"}`,
        onClick: open ? () => this.setState({ selectedDate: iso, selected: null, error: null }) : undefined
      });
    }

    const slots = selectedDate ? (days[selectedDate] || []) : [];
    const groups = twilight ? [["Twilight", slots]] : [
      ["Morning", slots.filter(s => s.slot < 12)],
      ["Afternoon", slots.filter(s => s.slot >= 12 && s.slot < 17)],
      ["Evening", slots.filter(s => s.slot >= 17)]
    ];
    const times = [];
    groups.forEach(([label, list]) => {
      if (list.length === 0) return;
      times.push(<div key={`g-${label}`} className="bkCal-group">{label}</div>);
      list.forEach(s => {
        const on = selected?.start_time === s.start_time;
        times.push(<button type="button" key={s.start_time} className={`bkCal-time${on ? " is-selected" : ""}`} aria-pressed={on ? "true" : "false"}
          onClick={() => this.setState({ selected: s, error: null })}>{formatTime(new Date(s.start_time), jsTz).replace(/^0/, "")}</button>);
      });
    });

    const anyOpen = Object.keys(days).length > 0;
    let emptyText = "Tap a date with a blue dot to see open times.";
    if (!loading && !anyOpen) emptyText = "No open times this month. Try the next month.";
    if (error) emptyText = error;

    const summary = selected
      ? `${formatDatetime(new Date(selected.start_time), "ddd, MMM D", tz)} · ${formatTime(new Date(selected.start_time), jsTz).replace(/^0/, "")}`
      : selectedDate ? `${formatDatetime(new Date(`${selectedDate}T12:00:00`), "ddd, MMM D")} · pick a time` : "Choose a date and time";

    return <BookingCalendar
      title={twilight ? "Pick a twilight date" : "Pick a date and time"}
      subtitle={this.props.photographerId
        ? "Showing your chosen photographer's open times."
        : "Only times a photographer can be there are shown."}
      onClose={this.props.onClose}
      monthLabel={month.toLocaleString("en-US", { month: "long", year: "numeric" })}
      onPrevMonth={() => this.changeMonth(-1)}
      onNextMonth={() => this.changeMonth(1)}
      prevDisabled={month <= this.firstMonth}
      days={cells}
      loadingDays={loading}
      showLegend
      timesHeading={selectedDate ? formatDatetime(new Date(`${selectedDate}T12:00:00`), "dddd, MMMM D") : null}
      timezoneNote={tz ? `Times are shown in ${tz}` : null}
      times={error ? [] : times}
      emptyTimesText={emptyText}
      footerLink={this.props.photographerId
        ? <button type="button" className="bkCal-link" onClick={this.props.onUseFirstAvailable}>Use first available instead</button>
        : <button type="button" className="bkCal-link" onClick={this.props.onChooseOwn}>Have a photographer in mind?</button>}
      summary={summary}
      applyLabel={assigning ? "Booking…" : "Continue"}
      onApply={this.confirm}
      applyDisabled={!selected || assigning}
    />;
  }
}
